import React, { useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import {
  Button,
  useNotificationService,
  useUserService,
} from '@abroad/components'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { COUNTRY_CODES } from '../../utils/constants'
import { gtmMakeExperiencePaymentAction } from './gtmfunctions'

const CheckoutForm = ({
  paymentMethod,
  onBack = () => {},
  billingAddress,
  showVerifyBillingInfo = false,
  onVerifyBillingInfo = () => {},
  onMakePayment = () => {},
  clientSecret,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const stripe = useStripe()
  const elements = useElements()
  const { user } = useUserService()
  const { questId } = useParams()
  const notification = useNotificationService()
  // const history = useHistory()
  const getPaymentStatusRedirectUrl = (status) => {
    const normalizedStatus = status === 'succeeded' ? 'succeeded' : 'pending'
    return `${
      process.env.REACT_APP_DOMAIN
    }/your-experiences/${questId}/payment-status?payment_intent_client_secret=${encodeURIComponent(
      clientSecret,
    )}&redirect_status=${normalizedStatus}`
  }

  const redirectToPaymentStatus = (status = 'pending') => {
    window.location.href = getPaymentStatusRedirectUrl(status)
  }

  const handleMicrodepositVerification = (paymentIntent) => {
    if (
      paymentIntent?.status === 'requires_action' &&
      paymentIntent?.next_action?.type === 'verify_with_microdeposits'
    ) {
      const hostedVerificationUrl =
        paymentIntent.next_action.verify_with_microdeposits
          ?.hosted_verification_url
      if (hostedVerificationUrl) {
        window.open(hostedVerificationUrl, '_blank', 'noopener,noreferrer')
      }
      redirectToPaymentStatus('pending')
      return true
    }
    return false
  }

  const handleSuccessfulOrProcessingPayment = (paymentIntent) => {
    if (
      paymentIntent?.status === 'succeeded' ||
      paymentIntent?.status === 'processing'
    ) {
      redirectToPaymentStatus(paymentIntent.status)
      return true
    }
    return false
  }

  const createPaymentMethodFromElements = async () => {
    const {
      error: createPaymentMethodError,
      paymentMethod: createdPaymentMethod,
    } = await stripe.createPaymentMethod({
      elements,
    })

    if (createPaymentMethodError || !createdPaymentMethod?.id) {
      setIsLoading(false)
      const errorMessage =
        createPaymentMethodError?.message || 'Unable to create payment method.'
      notification.showNotification(errorMessage, 'danger')
      return null
    }

    return createdPaymentMethod
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    gtmMakeExperiencePaymentAction(questId, paymentMethod, user?._id)
    setIsLoading(true)
    if (!stripe || !elements) return

    let result
    await elements.submit()
    // Use specific confirm methods for ACH and SEPA as per senior's requirements
    if (paymentMethod === 'ach') {
      // ACH: Use confirmUsBankAccountPayment
      const createdPaymentMethod = await createPaymentMethodFromElements()
      if (!createdPaymentMethod) return

      result = await stripe.confirmUsBankAccountPayment(clientSecret, {
        payment_method: createdPaymentMethod.id,
      })

      if (handleMicrodepositVerification(result?.paymentIntent)) return
      if (handleSuccessfulOrProcessingPayment(result?.paymentIntent)) return
    } else if (paymentMethod === 'sepa') {
      // SEPA: Use confirmSepaDebitPayment with an existing payment method created from the Payment Element
      const createdPaymentMethod = await createPaymentMethodFromElements()
      if (!createdPaymentMethod) return

      result = await stripe.confirmSepaDebitPayment(clientSecret, {
        payment_method: createdPaymentMethod.id,
      })

      if (handleSuccessfulOrProcessingPayment(result?.paymentIntent)) return
    } else {
      // For other methods (credit_card, klarna, etc.): Use the old unified confirmPayment
      result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${process.env.REACT_APP_DOMAIN}/your-experiences/${questId}/payment-status`,
        },
      })
    }

    setIsLoading(false)
    if (result?.error) {
      notification.showNotification(result.error.message, 'danger')
    } else {
      notification.showNotification('Payment succeeded!', 'success')
    }
  }

  // Helper function to safely convert values to strings
  const safeStringValue = (value) => {
    if (value === null || value === undefined) {
      return ''
    }
    return String(value)
  }

  const getStripeCountryCode = (value) => {
    const stringValue = safeStringValue(value).trim()

    if (!stringValue) {
      return ''
    }

    if (stringValue.length === 2) {
      return stringValue.toUpperCase()
    }

    const matchedCountry = COUNTRY_CODES.find(
      (country) => country.name.toLowerCase() === stringValue.toLowerCase(),
    )

    if (matchedCountry?.code) {
      return matchedCountry.code.toUpperCase()
    }

    return ''
  }

  const paymentElementOptions = {
    defaultValues: {
      billingDetails: {
        name: safeStringValue(user?.fullName),
        email: safeStringValue(user?.email),
        address: {
          country: getStripeCountryCode(billingAddress?.country),
        },
      },
    },
  }

  return (
    <form onSubmit={handleSubmit} className='mt-4'>
      {['credit_card', 'ach', 'klarna', 'wire_transfer', 'sepa'].includes(
        paymentMethod,
      ) && <PaymentElement options={paymentElementOptions} />}
      <Row className='d-flex'>
        {showVerifyBillingInfo ? (
          <>
            <Col xl={4} lg={4} md={4} sm={4} xs={4}>
              <Button
                className='text-uppercase payment-back-btn line-height-normal w-100 s9 mt-30px btn-fixed-height'
                disabled={isLoading}
                onClick={onBack}>
                Back
              </Button>
            </Col>
            <Col xl={8} lg={8} md={8} sm={8} xs={8}>
              <Button
                className='text-uppercase payment-back-btn line-height-normal w-100 s9 mt-30px btn-fixed-height'
                // variant='secondary'
                disabled={isLoading}
                onClick={onVerifyBillingInfo}>
                VERIFY BILLING INFO
              </Button>
            </Col>
            <Col xl={12} lg={12} md={12} sm={12} xs={12}>
              <Button
                type='submit'
                variant='saffron'
                className='text-uppercase line-height-normal w-100 s9 border-radius-normal mt-30px btn-fixed-height'
                isLoading={isLoading}
                disabled={!stripe}
                size='md'>
                MAKE PAYMENT
              </Button>
            </Col>
          </>
        ) : (
          <>
            <Col xl={4} lg={4} md={4} sm={4} xs={4}>
              <Button
                className='text-uppercase payment-back-btn line-height-normal w-100 s9 mt-30px btn-fixed-height'
                disabled={isLoading}
                onClick={onBack}>
                Back
              </Button>
            </Col>
            <Col xl={8} lg={8} md={8} sm={8} xs={8}>
              <Button
                type='submit'
                variant='saffron'
                className='text-uppercase line-height-normal w-100 s9 border-radius-normal mt-30px btn-fixed-height'
                isLoading={isLoading}
                disabled={!stripe}
                size='md'>
                MAKE PAYMENT
              </Button>
            </Col>
          </>
        )}
      </Row>
    </form>
  )
}

export default CheckoutForm
